import React, { useCallback, useState } from 'react';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { AgentArrivalHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/types';
import { AgentArrivalHypothesisMutationParams } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/hooks/agentArrivalHypothesis';
import AgentConfirmationModal from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/components/AgentConfirmationModal';
import { GridEchelons } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/helpers/types';
import AgentArrivalHypothesisBaseModal from './AgentArrivalHypothesisBaseModal';
import { useFormState } from '../hooks/agentArrival';

type Props = {
    deleteHypothesis: () => void;
    editHypothesis: (payload: AgentArrivalHypothesisMutationParams) => void;
    hrSimulation: HrSimulation;
    hypothesis: AgentArrivalHypothesis;
    officialGrades: GridEchelons[];
    onClose: () => void;
    show: boolean;
};

const AgentArrivalHypothesisUpdateModal = ({
    deleteHypothesis,
    editHypothesis,
    hrSimulation,
    hypothesis,
    officialGrades,
    onClose,
    show,
}: Props): React.ReactElement => {
    const [formState, setFormState] = useFormState({
        agentETP: hypothesis.etp,
        agentFirstName: hypothesis.agentFirstName,
        agentLastName: hypothesis.agentLastName,
        echelon: String(hypothesis.echelon),
        filiere: hypothesis.filiere,
        ifse: hypothesis.ifse,
        isFormValid: false,
        jobLabel: hypothesis.jobLabel,
        jobNumber: hypothesis.jobNumber,
        majoredSalary: hypothesis.salary,
        mantyStatus: hypothesis.mantyStatus,
        officialGrade: hypothesis.officialGrade,
        recurrence: hypothesis.recurrence,
        retirementSystem: hypothesis.retirementSystem,
        selectedEndDate: hypothesis.endDate ?? undefined,
        selectedOrgChartHrSoftwareId: hypothesis.orgChartHrSoftwareId,
        selectedStartDate: hypothesis.startDate,
    });
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const onDelete = () => {
        deleteHypothesis();
        setShowConfirmationModal(false);
        onClose();
    };
    const confirmationModal = (
        <AgentConfirmationModal
            onDelete={onDelete}
            showConfirmationModal={showConfirmationModal}
            setShowConfirmationModal={setShowConfirmationModal}
        />
    );
    const onValidate = useCallback(() => {
        const updatePayload = {
            agentFirstName: formState.agentFirstName,
            agentLastName: formState.agentLastName,
            echelon: Number(formState.echelon),
            endDate: formState.selectedEndDate,
            etp: formState.agentETP,
            filiere: formState.filiere,
            ifse: formState.ifse,
            jobLabel: formState.jobLabel ?? '',
            jobNumber: formState.jobNumber ?? '',
            mantyStatus: formState.mantyStatus,
            officialGrade: formState.officialGrade,
            orgChartHrSoftwareId: formState.selectedOrgChartHrSoftwareId,
            recurrence: formState.recurrence,
            retirementSystem: formState.retirementSystem,
            salary: formState.majoredSalary,
            startDate: formState.selectedStartDate ?? undefined,
        };
        editHypothesis(updatePayload);
    }, [formState, editHypothesis]);

    const onClickSave = () => {
        onValidate();
        onClose();
    };

    return (
        <>
            <AgentArrivalHypothesisBaseModal
                disableOrgChartSelector
                editMode
                formState={formState}
                hrSimulation={hrSimulation}
                onClickSave={onClickSave}
                onClose={onClose}
                officialGrades={officialGrades}
                onDelete={onDelete}
                orgChartHrs={hrSimulation.orgChartHrs.filter(
                    (och) => och.softwareId === hypothesis.orgChartHrSoftwareId,
                )}
                setFormState={setFormState}
                show={show}
            />
            {showConfirmationModal && confirmationModal}
        </>
    );
};

export default AgentArrivalHypothesisUpdateModal;
